import { ViewEmployee } from '@xeetr/types/models'
import { useEffect } from 'react'
import { FormProvider, useForm } from 'react-hook-form'

import EmployeeContent from './EmployeeContent'

import { EmployeeFormProps } from '@/types/EmployeeForm'

/**
 * Individual employee form with independent FormProvider
 * Each accordion item is its own form
 */
const EmployeeForm = ({
  employee,
  isExpanded,
  onRegisterForm,
  onUpdate,
  onCopy,
  onDelete,
}: EmployeeFormProps) => {
  const methods = useForm<ViewEmployee>({
    defaultValues: employee,
    mode: 'onChange', // Validate on change for immediate feedback
  })

  const { formState, handleSubmit, reset } = methods

  // Register/unregister form control when expanded state changes
  useEffect(() => {
    if (isExpanded) {
      const formHandle = {
        isDirty: formState.isDirty,
        isSaving: formState.isSubmitting,
        employeeId: employee.id,
        onSave: handleSubmit((data) => {
          onUpdate(data)
          // Reset form with new data to mark as clean
          reset(data)
        }),
        onDiscard: () => {
          reset(employee) // Reset to original employee data
        },
      }
      onRegisterForm(formHandle)
    } else {
      onRegisterForm(null)
    }
  }, [isExpanded, formState.isDirty, formState.isSubmitting, employee.id])

  // Update form control when dirty state changes (user types)
  useEffect(() => {
    if (isExpanded) {
      const formHandle = {
        isDirty: formState.isDirty,
        isSaving: formState.isSubmitting,
        employeeId: employee.id,
        onSave: handleSubmit((data) => {
          onUpdate(data)
          reset(data)
        }),
        onDiscard: () => {
          reset(employee)
        },
      }
      onRegisterForm(formHandle)
    }
  }, [formState.isDirty, formState.isSubmitting])

  // Sync form with external employee changes (e.g., after successful save)
  useEffect(() => {
    reset(employee)
  }, [employee])

  return (
    <FormProvider {...methods}>
      <form onSubmit={(e) => e.preventDefault()}>
        <EmployeeContent onCopy={onCopy} onDelete={onDelete} />
      </form>
    </FormProvider>
  )
}

export default EmployeeForm
